import {
  EditTracksInfoRequestData,
  LoadRequestData,
} from '../types/chromecast-caf-receiver/cast.framework.messages';
import {
  CastReceiverContext,
  PlaybackConfig,
} from '../types/chromecast-caf-receiver/cast.framework';
import { StatsPlugin } from './plugins/StatsPlugin';
import { YospacePlugin } from './plugins/YospacePlugin';
import { PlaybackPlugin } from './plugins/PlaybackPlugin';
import { DrmPlugin } from './plugins/DrmPlugin';
import { AppOptionsPlugin } from './plugins/AppOptionsPlugin';
import { CaptionsPlugin } from './plugins/CaptionsPlugin';
import { AutoNextPlugin } from './plugins/AutoNextPlugin';
import {
  appOptionsFilter,
  bootstrapAction,
  customMessageAction,
  editTracksInfoFilter,
  loadRequestFilter,
  playbackConfigFilter,
  playbackInfoHandlerFilter,
} from './extensions';
import { ConcurrencyPlugin } from './plugins/ConcurrencyPlugin';
import cast from '../types/chromecast-caf-receiver';
import { StylingPlugin } from './plugins/StylingPlugin';
import { SlideshowPlugin } from './plugins/SlideshowPlugin';
import { UnifiedPlayerPlugin } from './plugins/UnifiedPlayerPlugin';
import '../modules/nielsen-queue';
import '../config/adobe';
import { ReloadPlugin } from './plugins/ReloadPlugin';
import { LogProxyPlugin } from './plugins/LogProxyPlugin';
import { MetadataPlugin } from './plugins/MetadataPlugin';
import { DisableLiveSeekPlugin } from './plugins/DisableLiveSeekPlugin';
import { StallCheckerPlugin } from './plugins/StallCheckerPlugin';
import { isDev } from '../utils/env';
import { PluginInterface } from './plugins/PluginInterface';
import { StaticCommercialPlugin } from './plugins/StaticCommercialPlugin';
import { ErrorOverlayPlugin } from './plugins/ErrorOverlayPlugin';
import { LarOverlayPlugin } from './plugins/LarOverlayPlugin';
import { ErrorListenerPlugin } from './plugins/ErrorListenerPlugin';
import { ErrorsToSenderPlugin } from './plugins/ErrorsToSenderPlugin';
import { LogErrorsPlugin } from './plugins/LogErrorsPlugin';
import { Gen1DeprecatedError, ShutDownGen1Plugin } from './plugins/ShutDownGen1Plugin';
import { BusinessErrorPlugin } from './plugins/BusinessErrorPlugin';
import { CAST_MESSAGES } from '../config/receiver';
import { CustomMessageEvent } from '../types';

export const initReceiver = async () => {
  const plugins: PluginInterface[] = [
    new UnifiedPlayerPlugin(),
    new PlaybackPlugin(),
    new DrmPlugin(),
    new YospacePlugin(),
    new StatsPlugin(),
    new AppOptionsPlugin(),
    new CaptionsPlugin(),
    new AutoNextPlugin(),
    new ConcurrencyPlugin(),
    new SlideshowPlugin(),
    new StylingPlugin(),
    new MetadataPlugin(),
    new StallCheckerPlugin(),
    new DisableLiveSeekPlugin(),
    new StaticCommercialPlugin(),
    new ErrorListenerPlugin(),
    new ErrorOverlayPlugin(),
    new ErrorsToSenderPlugin(),
    new LogErrorsPlugin(),
    new LarOverlayPlugin(),
    new ShutDownGen1Plugin(),
    new BusinessErrorPlugin(),
    // new SeekHackPlugin(), // disabled because we hope it is no longer necessary
  ];

  const devPlugins: PluginInterface[] = [new ReloadPlugin(), new LogProxyPlugin()];

  if (isDev) {
    plugins.push(...devPlugins);
  }

  await new BootStrapper(plugins).init();
};

class BootStrapper {
  context: CastReceiverContext;
  constructor(plugins: PluginInterface[]) {
    this.context = cast.framework.CastReceiverContext.getInstance();
    plugins.forEach(plugin => plugin.init(this.context));
  }

  async init() {
    try {
      await bootstrapAction.perform(this.context);
    } catch (e) {
      if (e instanceof Gen1DeprecatedError) {
        return;
      }
      throw e;
    }
    this.context
      .getPlayerManager()
      .setMessageInterceptor(cast.framework.messages.MessageType.LOAD, async loadRequestData => {
        const result = await loadRequestFilter.apply(loadRequestData);
        const playbackConfig = await playbackConfigFilter.apply(
          new cast.framework.PlaybackConfig(),
          result
        );
        this.context.getPlayerManager().setPlaybackConfig(playbackConfig);
        return result;
      });

    this.context
      .getPlayerManager()
      .setMediaPlaybackInfoHandler(
        (loadRequest: LoadRequestData, playbackConfig: PlaybackConfig) => {
          return playbackInfoHandlerFilter.apply(playbackConfig, loadRequest);
        }
      );

    this.context
      .getPlayerManager()
      .setMessageInterceptor(
        cast.framework.messages.MessageType.EDIT_TRACKS_INFO,
        (request: EditTracksInfoRequestData) => {
          return editTracksInfoFilter.apply(request);
        }
      );

    this.context.addCustomMessageListener(CAST_MESSAGES.CUSTOM, request =>
      customMessageAction.perform(request as CustomMessageEvent)
    );

    this.context.start(await appOptionsFilter.apply(new cast.framework.CastReceiverOptions()));
  }
}
